import { Download, Link as LinkIcon, MoreHorizontal, Trash2 } from 'lucide-react';

import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@g17eco/ui-modern/components/ui/dropdown-menu';

import { EvidenceType, type EmissionEvidenceListItem } from '../../types/emissions';
import {
  EVIDENCE_TYPE_LABEL,
  EVIDENCE_TYPE_TINT,
  TINT_CLASSES,
  getMimeThumb,
} from '../../utils/evidenceDisplay';
import { bytesToHumanReadable } from '../../../../utils';
import { formatTimeAgo } from '../../../../utils/date';

const FileThumb = ({ mimeType, fileName }: { mimeType: string; fileName: string }) => {
  const { ext, tint, icon } = getMimeThumb(mimeType, fileName);
  const { bg, ink } = TINT_CLASSES[tint];
  return (
    <div
      className={`tw:relative tw:flex tw:h-12 tw:w-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded tw:border tw:border-border ${bg}`}
      aria-hidden
    >
      <i className={`fa-regular ${icon} tw:text-base ${ink}`} />
      <span className='tw:absolute tw:bottom-0.5 tw:left-0.5 tw:rounded-sm tw:px-1 tw:py-[1px] tw:text-[9px] tw:font-mono tw:font-semibold tw:tracking-wider tw:bg-slate-900 tw:text-white'>
        {ext}
      </span>
    </div>
  );
};

const CategoryChip = ({ evidenceType }: { evidenceType: EvidenceType }) => {
  const { bg, ink } = TINT_CLASSES[EVIDENCE_TYPE_TINT[evidenceType]];
  return (
    <Badge variant='ghost' className={`tw:text-[10px] tw:border-transparent ${bg} ${ink}`}>
      {EVIDENCE_TYPE_LABEL[evidenceType]}
    </Badge>
  );
};

interface EvidenceLibraryRowProps {
  evidence: EmissionEvidenceListItem;
  selected: boolean;
  selectable: boolean;
  onToggleSelect: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onShowLinks: () => void;
}

export const EvidenceLibraryRow = ({
  evidence,
  selected,
  selectable,
  onToggleSelect,
  onDownload,
  onDelete,
  onShowLinks,
}: EvidenceLibraryRowProps) => {
  // Linked evidence is referenced by one or more activities — deleting it
  // would orphan their evidenceData rows. Block at the UI; gives the user
  // a clear reason instead of letting the request fail server-side.
  const isLinked = evidence.linkedActivityCount > 0;
  return (
  <li
    className={`tw:flex tw:items-center tw:gap-3 tw:rounded-md tw:border tw:p-3 tw:transition-colors ${
      selected ? 'tw:border-emerald-300 tw:bg-emerald-50/40' : 'tw:border-border tw:bg-background'
    }`}
  >
    {selectable ? (
      <input
        type='checkbox'
        aria-label={`Select ${evidence.originalName}`}
        checked={selected}
        onChange={onToggleSelect}
        className='tw:h-4 tw:w-4 tw:cursor-pointer tw:accent-emerald-600'
      />
    ) : null}
    <FileThumb mimeType={evidence.mimeType} fileName={evidence.originalName} />
    <div className='tw:flex-1 tw:min-w-0'>
      <div className='tw:flex tw:flex-wrap tw:items-center tw:gap-2'>
        <span className='tw:truncate tw:text-sm tw:font-medium'>{evidence.originalName}</span>
        <CategoryChip evidenceType={evidence.evidenceType} />
        {isLinked ? (
          <button
            type='button'
            onClick={onShowLinks}
            aria-label={`Show ${evidence.linkedActivityCount} linked activities for ${evidence.originalName}`}
            className='tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0'
          >
            <Badge
              variant='outline'
              className='tw:text-[10px] tw:border-emerald-300 tw:text-emerald-800 tw:hover:bg-emerald-50 tw:transition-colors'
            >
              <LinkIcon className='tw:h-2.5 tw:w-2.5' />
              linked to {evidence.linkedActivityCount}
            </Badge>
          </button>
        ) : (
          <Badge variant='ghost' className='tw:text-[10px] tw:text-ThemeTextLight'>
            unlinked
          </Badge>
        )}
      </div>
      <div className='tw:text-xs tw:text-ThemeTextLight tw:mt-0.5'>
        <span className='tw:font-mono'>{bytesToHumanReadable(evidence.fileSize)}</span>
        {' · uploaded '}
        {formatTimeAgo(evidence.created)}
        {' by '}
        {evidence.uploadedByName}
        {evidence.issuer ? ` · ${evidence.issuer}` : ''}
      </div>
    </div>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='icon-sm'
          aria-label={`Actions for ${evidence.originalName}`}
        >
          <MoreHorizontal className='tw:h-4 tw:w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem onClick={onDownload}>
          <Download className='tw:h-3.5 tw:w-3.5' />
          Download
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant='destructive'
          onClick={onDelete}
          disabled={isLinked}
        >
          <Trash2 className='tw:h-3.5 tw:w-3.5' />
          {isLinked ? 'Delete (detach activities first)' : 'Delete document'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </li>
  );
};
